import axios, { AxiosError, AxiosResponse, Method } from "axios";
import { ref } from "vue";

const serviceUnavailable = ref(false);
const apiTokenKey = "nl_token";

const setTokenFromResponse = (resp: AxiosResponse) => {
  if (resp?.headers["authorization"]) {
    setToken(resp.headers["authorization"]);
  }
};

const getToken = () => sessionStorage.getItem(apiTokenKey);
const setToken = (value: string) => sessionStorage.setItem(apiTokenKey, value);
const removeToken = () => sessionStorage.removeItem(apiTokenKey);

const useApi = () => {
  const baseUrl = process.env.VUE_APP_BASE_API_URL
    ? process.env.VUE_APP_BASE_API_URL
    : "/api/v1";

  const post = <T>(
    url: string,
    data: unknown,
    useBaseUrl = true
  ): Promise<AxiosResponse<T>> => {
    return execute<T>(
        axios.post<T>(
          `${useBaseUrl ? baseUrl : ""}${url}`,
          data,
          getRequestConfig("POST", data)
        )
      );
  };

  const get = <T>(
    url: string,
    data: unknown,
    useBaseUrl = true
  ): Promise<AxiosResponse<T>> => {
    return execute<T>(
        axios.get<T>(
          `${useBaseUrl ? baseUrl : ""}${url}`,
          getRequestConfig("GET", data)
        )
      );
  };

  const del = <T>(
    url: string,
    data: unknown,
    useBaseUrl = true
  ): Promise<AxiosResponse<T>> => {
    return execute<T>(
        axios.delete<T>(
          `${useBaseUrl ? baseUrl : ""}${url}`,
          getRequestConfig("DELETE", data)
        )
      );
  };

  const put = <T>(
    url: string,
    data: unknown,
    useBaseUrl = true
  ): Promise<AxiosResponse<T>> => {
    return execute<T>(
        axios.put<T>(
          `${useBaseUrl ? baseUrl : ""}${url}`,
          data,
          getRequestConfig("PUT", data)
        )
      );
  };

  const execute = <T>(
    fetcher: Promise<AxiosResponse<unknown>>,
  ): Promise<AxiosResponse<T>> => {
    return new Promise((resolve, reject) => {
      fetcher
        .then((resp) => {
          setTokenFromResponse(resp);
          resolve(resp as AxiosResponse<T>);
        })
        .catch((err) => {
          if (err?.response?.status == 302) {
            debugger;
            setTokenFromResponse(err.response);
            resolve(err.response as AxiosResponse<T>);
          }
          if (err?.response?.status === 401) {
            removeToken();
            sessionStorage.clear();
          } else if (err?.response?.status === 404) {
            // do nothing, let reject handle
          }
          reject(err);
        }) as Promise<AxiosResponse<T> | void>;
    });
  };

  const delay = (t: number, value: unknown = undefined) => {
    return new Promise(function (resolve) {
      setTimeout(resolve.bind(null, value), t);
    });
  };

  type PromiseFunction<T> = () => Promise<T>;

  const retry = <T>(call: PromiseFunction<T>, count = 3): Promise<T> => {
    let lastError: AxiosError;
    return new Promise((resolve, reject) => {
      if (count > 0) {
        call()
          .then((data: T) => resolve(data))
          .catch((err) => {
            lastError = err;
            delay(500).then(() => retry(call, count - 1));
          });
      } else {
        reject(lastError);
      }
    });
  };

  const getRequestConfig = (method: Method, data: unknown) => {
    if (method == "GET") {
      return { ...getHeaders(), params: data };
    }
    return { ...getHeaders() };
  };

  const getHeaders = () => {
    const token = getToken();
    return {
      headers: {
        ...(token
          ? {
              authorization: token,
            }
          : {}),
      },
    };
  };

  return {
    post,
    get,
    del,
    put,
    delay,
    retry,
    serviceUnavailable,
  };
};

export default useApi;
